import { without } from 'ramda';
import { FOfferType, isAvailableOfferTypeCombination } from '../../packages/JsonQuery';
import { useCallback } from 'react';
import { useFilterChangeCallback } from 'shared/hooks';
import { useSelector } from 'react-redux';
import { selectOfferTypeValue } from 'shared/selectors/filters';
import { selectSpecialtyTypes } from 'shared/selectors/jsonQuery/selectSpecialtyTypes';
import { TFilter, TModifier } from 'shared/types/filters';

const BUSINESS_SUB_TYPES = FOfferType.RentalBusiness | FOfferType.ReadyBusiness;

export const useOfferTypeChangeHandler = (filterKey?: TFilter) => {
  const selectedOfferTypes = useSelector(selectOfferTypeValue);
  const specialtyTypes = useSelector(selectSpecialtyTypes);

  const onChange = useFilterChangeCallback(filterKey);

  return useCallback(
    (newSelectedValues: FOfferType[]) => {
      const isSelectedOptionsShorter = selectedOfferTypes.length < newSelectedValues.length;
      const primaryOptions = isSelectedOptionsShorter ? selectedOfferTypes : newSelectedValues;
      const secondaryOptions = isSelectedOptionsShorter ? newSelectedValues : selectedOfferTypes;

      const newSelectedOfferType = without(primaryOptions, secondaryOptions)[0];

      const combinedOfferType = [...selectedOfferTypes, newSelectedOfferType].reduce(
        (previousValue, currentValue) => previousValue | currentValue,
        FOfferType.Unexpected,
      );

      let nextOfferTypes;

      if (selectedOfferTypes.includes(newSelectedOfferType) && selectedOfferTypes.length > 1) {
        nextOfferTypes = selectedOfferTypes.filter(offerType => offerType !== newSelectedOfferType);
      } else if (isAvailableOfferTypeCombination(combinedOfferType)) {
        nextOfferTypes = [...selectedOfferTypes, newSelectedOfferType];
      } else {
        nextOfferTypes = [newSelectedOfferType];
      }

      const nextOfferType = nextOfferTypes.reduce(
        (previousValue, currentValue) => previousValue | currentValue,
        FOfferType.Unexpected,
      );

      const modifiers: [TModifier, ...TModifier[]] = [{ action: 'setOfferType', arguments: [nextOfferType] }];

      const wasBusiness = selectedOfferTypes.some(t => (t & BUSINESS_SUB_TYPES) !== 0);
      const isBusiness = (nextOfferType & BUSINESS_SUB_TYPES) !== 0;

      if (wasBusiness && !isBusiness && specialtyTypes && specialtyTypes.length > 0) {
        modifiers.push({ action: 'setSpecialtyTypes', arguments: [null] });
      }

      onChange(...modifiers);
    },
    [onChange, selectedOfferTypes, specialtyTypes],
  );
};
